import React from 'react';
import { Modal, Button, Space, Tag, Divider } from 'antd';
import { 
  FileTextOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  EyeOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  BookOutlined
} from '@ant-design/icons';
import MarkdownRenderer from './MarkdownRenderer';

const MarkdownViewer = ({ 
  visible, 
  onClose, 
  document, 
  onEdit,
  onDownload 
}) => {
  if (!document) return null;

  // 解析metadata
  let metadata = {};
  try {
    metadata = JSON.parse(document.metadata || '{}');
  } catch (e) {
    console.warn('解析metadata失败:', e);
  }

  const title = metadata.title || document.title || document.file_name?.replace(/\.[^/.]+$/, '') || '未命名文档';
  const content = document.content || '';
  const fileSize = document.size || 0;
  const createdAt = document.inserted_at || document.inserted_at;
  const updatedAt = document.updated_at;
  const description = document.description || '';

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ top: 20 }}
      footer={null}
      className="markdown-viewer-modal"
      bodyStyle={{ padding: 0, height: '85vh' }}
    >
      <div className="flex flex-col h-full bg-gray-50">
        {/* 头部信息区域 */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* 文档标题 */}
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                  <FileTextOutlined className="text-blue-600 text-lg" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    {title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <BookOutlined />
                      Markdown文档
                    </span>
                    <span className="flex items-center gap-1">
                      <CalendarOutlined />
                      创建于 {formatDate(createdAt)}
                    </span>
                    {updatedAt && updatedAt !== createdAt && (
                      <span className="flex items-center gap-1">
                        <CalendarOutlined />
                        更新于 {formatDate(updatedAt)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* 文档信息标签 */}
              <div className="flex items-center gap-3">
                <Tag color="blue" className="flex items-center gap-1">
                  <FileTextOutlined />
                  {formatFileSize(fileSize)}
                </Tag>
                {document.status && (
                  <Tag 
                    color={document.status === 'permanent' ? 'green' : 
                           document.status === 'temporary' ? 'orange' : 'default'}
                    className="flex items-center gap-1"
                  >
                    {document.status === 'permanent' ? '永久' : 
                     document.status === 'temporary' ? '临时' : '已归档'}
                  </Tag>
                )}
                {description && (
                  <span className="text-sm text-gray-600 max-w-md truncate">
                    {description}
                  </span>
                )}
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex items-center gap-2 ml-4">
              <Button
                type="text"
                icon={<EyeOutlined />}
                className="flex items-center gap-1 text-gray-600 hover:text-blue-600"
              >
                预览
              </Button>
              {onEdit && (
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={onEdit}
                  className="flex items-center gap-1"
                >
                  编辑
                </Button>
              )}
              {onDownload && (
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={onDownload}
                  className="flex items-center gap-1 text-gray-600 hover:text-green-600"
                >
                  下载
                </Button>
              )}
              <Button
                type="text"
                icon={<ShareAltOutlined />}
                className="flex items-center gap-1 text-gray-600 hover:text-purple-600"
              >
                分享
              </Button>
            </div>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full bg-white">
            {/* 内容容器 */}
            <div className="h-full overflow-auto">
              <div className="max-w-4xl mx-auto px-8 py-8">
                {/* 文档内容 */}
                <div className="prose prose-lg max-w-none">
                  <MarkdownRenderer 
                    content={content}
                    className="markdown-content"
                  />
                </div>

                {/* 文档底部信息 */}
                <Divider />
                <div className="flex items-center justify-between text-sm text-gray-500 mt-8">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <UserOutlined />
                      文档ID: {document.id}
                    </span>
                    <span className="flex items-center gap-1">
                      <FileTextOutlined />
                      字符数: {content.length}
                    </span>
                  </div>
                  <div className="text-xs">
                    最后查看: {formatDate(new Date())}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .markdown-viewer-modal .ant-modal-content {
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          
          .markdown-viewer-modal .ant-modal-body {
            flex: 1;
            padding: 0;
          }

          .markdown-content {
            font-size: 16px;
            line-height: 1.7;
            color: #374151;
          }

          .markdown-content h1,
          .markdown-content h2,
          .markdown-content h3,
          .markdown-content h4,
          .markdown-content h5,
          .markdown-content h6 {
            margin-top: 2rem;
            margin-bottom: 1rem;
            font-weight: 600;
            line-height: 1.4;
            color: #111827;
          }

          .markdown-content h1 {
            font-size: 2.25rem;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 0.5rem;
          }

          .markdown-content h2 {
            font-size: 1.875rem;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 0.5rem;
          }

          .markdown-content h3 {
            font-size: 1.5rem;
          }

          .markdown-content h4 {
            font-size: 1.25rem;
          }

          .markdown-content p {
            margin: 1rem 0;
            line-height: 1.7;
          }

          .markdown-content ul,
          .markdown-content ol {
            margin: 1rem 0;
            padding-left: 1.5rem;
          }

          .markdown-content li {
            margin: 0.5rem 0;
            line-height: 1.6;
          }

          .markdown-content code {
            background-color: #f3f4f6;
            color: #1f2937;
            padding: 0.25rem 0.5rem;
            border-radius: 0.375rem;
            font-size: 0.875rem;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          }

          .markdown-content pre {
            background-color: #1f2937;
            color: #f9fafb;
            border-radius: 0.5rem;
            padding: 1rem;
            margin: 1.5rem 0;
            overflow: auto;
          }

          .markdown-content pre code {
            background-color: transparent;
            color: inherit;
            padding: 0;
            border: none;
            font-size: 0.875rem;
            line-height: 1.5;
          }

          .markdown-content a {
            color: #3b82f6;
            text-decoration: none;
            border-bottom: 1px solid transparent;
            transition: border-color 0.2s;
          }

          .markdown-content a:hover {
            border-bottom-color: #3b82f6;
          }

          .markdown-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 1.5rem 0;
            border: 1px solid #e5e7eb;
            border-radius: 0.5rem;
            overflow: hidden;
          }

          .markdown-content th,
          .markdown-content td {
            border: 1px solid #e5e7eb;
            padding: 0.75rem 1rem;
            text-align: left;
          }

          .markdown-content th {
            background-color: #f9fafb;
            font-weight: 600;
            color: #374151;
          }

          .markdown-content blockquote {
            border-left: 4px solid #3b82f6;
            margin: 1.5rem 0;
            padding: 1rem 1.5rem;
            background-color: #f8fafc;
            color: #64748b;
            font-style: italic;
            border-radius: 0 0.5rem 0.5rem 0;
          }

          .markdown-content hr {
            border: none;
            border-top: 1px solid #e5e7eb;
            margin: 2rem 0;
          }

          .markdown-content strong {
            font-weight: 600;
          }

          .markdown-content em {
            font-style: italic;
          }

          .markdown-content del {
            text-decoration: line-through;
            color: #6b7280;
          }

          .markdown-content img {
            max-width: 100%;
            height: auto;
            border-radius: 0.5rem;
            margin: 1rem 0;
          }

          .markdown-content .highlight {
            background-color: #fef3c7;
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
          }
        `
      }} />
    </Modal>
  );
};

export default MarkdownViewer;
